import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Navigate } from 'react-router-dom';
import HubPageLayout from '../components/HubPageLayout';
import FramedPanel from '../components/FramedPanel';
import VideoPlayerModal from '../components/VideoPlayerModal';
import { sectionBySlug, DEFAULT_SECTION, thumbnailUrl } from './data/videos';

// Av Library video section (FBI / IC3, SSA, IRS, FTC): framed hero titled
// with the agency over a grid of that agency's videos. Selecting one plays it in an
// overlay. The section comes from the route: /dashboard/av-library/:section.

const PlayBadge = () => (
  <span className="absolute inset-0 flex items-center justify-center opacity-85 transition-opacity group-hover:opacity-100">
    <span className="flex items-center justify-center rounded-full bg-black/55 w-12 h-12">
      <span
        className="block border-y-transparent border-l-white"
        style={{ borderStyle: 'solid', borderWidth: '8px 0 8px 13px', marginLeft: 3 }}
      />
    </span>
  </span>
);

const VideoSectionPage = () => {
  const { section: slug } = useParams();
  const section = sectionBySlug(slug);
  const videos = section ? section.videos : [];

  const [selected, setSelected] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Deep link from the Av Library dropdown elsewhere in the hub:
  // /dashboard/av-library/<section>?video=<videoId> opens that video's player.
  // Switching sections without a param closes any open player.
  const requestedVideoId = searchParams.get('video');
  useEffect(() => {
    const list = section ? section.videos : [];
    const match = requestedVideoId ? list.find((v) => v.videoId === requestedVideoId) : null;
    setSelected(match || null);
  }, [requestedVideoId, section]);

  if (!section) return <Navigate to={`/dashboard/av-library/${DEFAULT_SECTION}`} replace />;

  // Drop the param when the player closes so a refresh doesn't reopen it.
  const closePlayer = () => {
    setSelected(null);
    if (searchParams.has('video')) {
      const next = new URLSearchParams(searchParams);
      next.delete('video');
      setSearchParams(next, { replace: true });
    }
  };

  return (
    <HubPageLayout>
      <FramedPanel title={section.label}>
        <div className="px-6 pt-8 pb-6">
          <img src={section.hero} alt="" className="w-full h-[300px] object-cover" />
        </div>
      </FramedPanel>

      {videos.length === 0 ? (
        <p className="mt-8 text-center text-black">{section.label} videos are coming soon.</p>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
          {videos.map((video) => (
            <button
              key={video.videoId}
              type="button"
              onClick={() => setSelected(video)}
              className="group text-left"
              aria-label={`Play ${video.title}`}
            >
              <div className="relative w-full overflow-hidden bg-black" style={{ aspectRatio: '16 / 9' }}>
                <img
                  src={thumbnailUrl(video)}
                  alt=""
                  className="w-full h-full object-cover transition-transform group-hover:scale-[1.03]"
                />
                <PlayBadge />
              </div>
              <p className="mt-2 text-black">{video.title}</p>
            </button>
          ))}
        </div>
      )}

      <VideoPlayerModal video={selected} onClose={closePlayer} />
    </HubPageLayout>
  );
};

export default VideoSectionPage;
